import React, { useEffect } from 'react';
import { View } from 'react-native';
import * as ScreenOrientation from 'expo-screen-orientation';
import { Platform } from 'react-native';
import { useKeepAwake } from 'expo-keep-awake';
import { restoreAuth, refreshAuth } from './src/lib/auth';
import { pb } from './src/lib/pb';
import { MainKioskScreen } from './src/components/MainKioskScreen';
import { LoginScreen } from './src/components/LoginScreen';
import { UserLoggedInScreen } from './src/components/UserLoggedInScreen';
import { UserLoginScreen } from './src/components/UserLoginScreen';

const DEVICE_ID = process.env.EXPO_PUBLIC_DEVICE_ID || '';

export default function App() {
  useKeepAwake();
  const [deviceId, setDeviceId] = React.useState<string | null>(null);
  const [mode, setMode] = React.useState<'deviceLogin' | 'userLogin'>('deviceLogin');
  const [authTick, setAuthTick] = React.useState(0);
  const [ready, setReady] = React.useState(false);
  useEffect(() => {
    // Web-only: ensure the app is installable as a PWA by registering a SW and
    // linking the manifest. (Expo Metro web doesn't provide turnkey SW support.)
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      try {
        const head = window.document?.head;
        if (head) {
          // Manifest link
          const existingManifest = head.querySelector('link[rel="manifest"]') as HTMLLinkElement | null;
          if (!existingManifest) {
            const link = window.document.createElement('link');
            link.rel = 'manifest';
            link.href = '/manifest.json';
            head.appendChild(link);
          }

          // iOS install hints (best-effort)
          const addMeta = (name: string, content: string) => {
            if (head.querySelector(`meta[name="${name}"]`)) return;
            const m = window.document.createElement('meta');
            m.name = name;
            m.content = content;
            head.appendChild(m);
          };
          addMeta('theme-color', '#111111');
          addMeta('apple-mobile-web-app-capable', 'yes');
          addMeta('apple-mobile-web-app-status-bar-style', 'black-translucent');
          addMeta('apple-mobile-web-app-title', 'emoji-kiosk');

          if (!head.querySelector('link[rel="apple-touch-icon"]')) {
            const icon = window.document.createElement('link');
            icon.rel = 'apple-touch-icon';
            icon.href = '/icons/icon-192.png';
            head.appendChild(icon);
          }
        }

        // Service worker registration (secure origins only)
        const isSecure = window.location.protocol === 'https:' || window.location.hostname === 'localhost';
        if (isSecure && 'serviceWorker' in navigator) {
          window.addEventListener('load', () => {
            navigator.serviceWorker.register('/sw.js').catch(() => {});
          });
        }
      } catch {}
    }

    if (Platform.OS !== 'web') {
      ScreenOrientation.lockAsync(ScreenOrientation.OrientationLock.LANDSCAPE);
    }
    (async () => {
      console.log('[app] boot');
      await restoreAuth();
      await refreshAuth();
      const model: any = pb.authStore.model as any;
      const restoredId = model?.id as string | undefined;
      if (restoredId && model?.collectionName === 'devices') {
        console.log('[app] restored session for device', restoredId);
        setDeviceId(restoredId);
      } else if (restoredId && model?.collectionName === 'users') {
        console.log('[app] restored session for user', restoredId);
      } else {
        console.log('[app] no saved session');
      }
      setReady(true);
    })();
  }, []);

  // React to auth changes (login/logout or refresh) to re-render immediately
  useEffect(() => {
    const unsub = pb.authStore.onChange(() => {
      setAuthTick((t) => t + 1);
    });
    return () => { try { (unsub as any)?.(); } catch {} };
  }, []);

  // Background safety net: refresh auth every 28 days while app is open
  useEffect(() => {
    const twentyEightDaysMs = 28 * 24 * 60 * 60 * 1000;
    const id = setInterval(() => {
      refreshAuth();
    }, twentyEightDaysMs);
    return () => clearInterval(id);
  }, []);

  if (!ready) return <View style={{ flex: 1 }} />;

  // Depend on authTick to recompute on auth changes
  const model: any = pb.authStore.model as any; void authTick;
  const isUser = Boolean(model && model.collectionName === 'users');
  const isDevice = Boolean(deviceId && model && model.collectionName === 'devices');

  return (
    <View style={{ flex: 1 }}>
      {isUser ? (
        <UserLoggedInScreen onLogout={() => { setDeviceId(null); setMode('deviceLogin'); }} />
      ) : isDevice ? (
        <MainKioskScreen deviceId={deviceId as string} onLogout={() => { setDeviceId(null); setMode('deviceLogin'); }} />
      ) : mode === 'userLogin' ? (
        <UserLoginScreen onSuccess={() => { /* user model already in authStore */ }} onBackToDeviceLogin={() => setMode('deviceLogin')} />
      ) : (
        <LoginScreen onSuccess={(id) => setDeviceId(id)} onSwitchToUserLogin={() => setMode('userLogin')} />
      )}
    </View>
  );
}